// In-app manual. Always-on overlay reachable from the header on every tab.
// Content is written for landlords, not engineers — keep edits in this same
// voice. If a section grows substantially, consider promoting it to its own
// sub-component, but for now everything lives here so updates are one file.

import { useEffect } from 'react';

export default function HelpModal({ onClose }) {
  // Esc closes; body scroll lock while modal is open so the underlying app
  // doesn't get the wheel events.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="LandlordLedger manual"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 300, padding: 12,
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0a0d16', border: '1px solid #1e2235',
          borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '92vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
        {/* Sticky header */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid #1e2235',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#0f1117', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'Courier New', monospace", letterSpacing: 1.2 }}>📖 MANUAL</div>
            <div style={{ fontSize: 18, color: '#fff', fontWeight: 700, marginTop: 2 }}>
              Landlord<span style={{ color: '#3b82f6' }}>Ledger</span> — How It Works
            </div>
          </div>
          <button onClick={onClose}
            aria-label="Close manual"
            style={{ background: '#1e2235', border: '1px solid #2d3555', borderRadius: 8, color: '#e2e8f0', padding: '6px 12px', fontSize: 14, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '20px 22px 28px', flex: 1 }}>

          <Section title="Welcome to LandlordLedger">
            <P>LandlordLedger is property accounting built for landlords, not accountants. No spreadsheets, no QuickBooks workarounds — just clean financials for your rental portfolio.</P>
            <P>Here's everything you need to know to get the most out of it.</P>
          </Section>

          <Section title="Dashboard" emoji="📊">
            <P>Your command center. The Dashboard gives you a real-time snapshot of your entire portfolio.</P>
            <UL>
              <LI><B>Total Income</B> — All rent and other income collected across every property, year-to-date.</LI>
              <LI><B>Total Expenses</B> — Every expense logged across all properties, year-to-date.</LI>
              <LI><B>Net Profit / Loss (YTD)</B> — Your bottom line. Income minus expenses for the year so far.</LI>
              <LI><B>Portfolio Cards</B> — Each property shows its own income, expenses, and net P&L at a glance. Scroll down to see all your properties.</LI>
            </UL>
            <Tip>Hit the Refresh button if your numbers ever look off — it recalculates everything from scratch.</Tip>
          </Section>

          <Section title="Transactions" emoji="💵">
            <P>This is where every dollar in and out gets recorded.</P>
            <UL>
              <LI><B>Add a Transaction</B> — Tap the + button to log income (rent payment, late fee, etc.) or an expense (repair, insurance, supplies, etc.).</LI>
              <LI><B>Assign to a Property</B> — Every transaction gets assigned to a specific property so your per-property P&L stays accurate.</LI>
              <LI><B>Categories</B> — Each transaction gets a category (Rent, Repairs & Maintenance, Insurance, Utilities, etc.). These map directly to IRS Schedule E so your tax report is always ready.</LI>
              <LI><B>Edit or Delete</B> — Tap any transaction to edit or remove it.</LI>
              <LI><B>Find Duplicates</B> — Tap the Find Duplicates button to scan for accidental duplicate entries. Same property, same month, similar amount — the app flags them and lets you delete the duplicate one at a time.</LI>
            </UL>
          </Section>

          <Section title="Import" emoji="📥">
            <P>The fastest way to get a lot of transactions in at once.</P>

            <SubSection title="Smart Upload (Recommended)">
              <P>Tap <B>Smart Upload</B> or <B>Take Photo</B> and let the AI handle it. It reads the document, figures out what it is, and does the right thing automatically:</P>
              <UL>
                <LI><B>Receipt or invoice</B> → Extracts the transaction and files the document in your property vault under Receipts.</LI>
                <LI><B>Bank statement</B> → Extracts all transactions and loads them for you to review and confirm. Option to also file the statement in your vault.</LI>
                <LI><B>Lease, inspection report, insurance policy, or other document</B> → Files it in the correct subfolder of your property vault. No transaction created.</LI>
                <LI><B>Not sure what it is?</B> → AI asks you which property and subfolder to use.</LI>
              </UL>
              <P>The AI suggests a clean file name for everything it uploads. You can edit it before saving.</P>
              <Tip><B>Free trial:</B> 10 Smart Upload scans included. Unlimited on Pro.</Tip>
            </SubSection>

            <SubSection title="Advanced — CSV / Excel Import">
              <P>If you export transactions from your bank or another tool:</P>
              <OL>
                <LI>Tap <B>Advanced</B></LI>
                <LI>Upload your CSV or Excel file, or paste the data directly</LI>
                <LI>AI parses the columns and maps them to LandlordLedger's format</LI>
                <LI>Review the transactions before confirming</LI>
              </OL>
              <Tip><B>Free trial:</B> 50 imported transaction rows included. Unlimited on Pro.</Tip>
            </SubSection>
          </Section>

          <Section title="Monthly P&L" emoji="📈">
            <P>See how your portfolio performed month by month.</P>
            <UL>
              <LI><B>Actual by Month chart</B> — Bar chart showing income vs expenses for each month of the year.</LI>
              <LI><B>6-Month Projection</B> — Based on your recurring transactions, see what your next 6 months should look like financially.</LI>
              <LI><B>Month by Month breakdown</B> — Scroll down to see each month's income, expenses, and net profit in detail.</LI>
            </UL>
            <Tip>If your projection looks off, check your Recurring tab — that's where the projected numbers come from.</Tip>
          </Section>

          <Section title="Recurring" emoji="🔁">
            <P>Set it and forget it for transactions that happen every month.</P>
            <UL>
              <LI><B>Add a Recurring Transaction</B> — Things like monthly rent, mortgage payments, insurance premiums, property management fees. Log it once and it automatically generates a transaction every month.</LI>
              <LI><B>Edit</B> — Update the amount or details anytime (e.g., rent increase).</LI>
              <LI><B>Delete</B> — Removes the recurring schedule. You'll be asked whether to also remove previously generated transactions or just stop future ones.</LI>
              <LI><B>Find Duplicates</B> — Same as Transactions — scans for accidental duplicates in your recurring list.</LI>
            </UL>
            <Tip>Recurring transactions are the backbone of your 6-month projection on the Monthly P&L tab. The more accurate your recurring list, the more accurate your forecast.</Tip>
          </Section>

          <Section title="Notes" emoji="📝">
            <P>A scratchpad for each property or your portfolio overall.</P>
            <UL>
              <LI>Log anything that doesn't fit neatly into a transaction — tenant conversations, maintenance reminders, things to follow up on, lease renewal dates.</LI>
              <LI>Notes are searchable and stay organized by property.</LI>
            </UL>
          </Section>

          <Section title="Properties" emoji="🏠">
            <P>Manage your rental portfolio.</P>
            <UL>
              <LI><B>Add a Property</B> — Name, address, property type (Residential, Commercial, Airbnb, etc.), and tenant notes.</LI>
              <LI><B>Per-Property P&L</B> — Each property card shows its gross income, expenses, and net profit so you always know which properties are performing.</LI>
              <LI><B>Edit</B> — Update property details anytime.</LI>
              <LI><B>Document Vault</B> — Tap the 📁 Vault button on any property to open its document vault (see below).</LI>
            </UL>

            <SubSection title="Document Vault">
              <P>Every property has its own secure document vault with 9 subfolders:</P>
              <VaultList rows={[
                ['📄 Receipts',          'Repair receipts, supply invoices, contractor bills'],
                ['📋 Leases',            'Current and past lease agreements'],
                ['🏠 Property Docs',     'Title, deed, abstract, survey'],
                ['🔑 Closing Documents', 'HUD settlement, purchase agreement'],
                ['🛡️ Insurance',         'Policy documents, claims'],
                ['🔧 Warranties',        'Appliances, roof, HVAC warranties'],
                ['🏛️ Tax Documents',     'Property tax bills, depreciation schedules'],
                ['📸 Photos',            'Move-in/move-out condition photos'],
                ['🔍 Inspections',       'Inspection reports, permits'],
              ]} />
              <P><B>To upload:</B> Navigate into a subfolder and tap Upload File or Take Photo. When you already know where a file belongs, upload it directly here — no AI call needed.</P>
              <P><B>To export:</B> Select individual files, entire subfolders, or the full vault and export as ZIP.</P>
              <Tip><B>Free trial:</B> 100MB storage per account. Unlimited on Pro.</Tip>
            </SubSection>
          </Section>

          <Section title="Tax Report" emoji="🏛️">
            <P>Tax time made easy.</P>
            <UL>
              <LI><B>Schedule E — Deductible Expenses</B> — Every expense category mapped to IRS Schedule E, totaled up year-to-date. Hand this to your CPA and they'll love you.</LI>
              <LI><B>Export as PDF</B> — Print-ready tax report in one tap.</LI>
              <LI><B>Export All Transactions as CSV</B> — Full transaction history for your accountant or records.</LI>
            </UL>
            <Note>Always consult your CPA before filing. LandlordLedger organizes your numbers — your tax professional makes the final call.</Note>
          </Section>

          <Section title="Pro Features" emoji="⭐">
            <P>Upgrade to Pro to unlock:</P>
            <UL>
              <LI>Unlimited Smart Upload document scans</LI>
              <LI>Unlimited CSV/Excel import rows</LI>
              <LI>Unlimited document vault storage</LI>
              <LI>All future Pro features</LI>
            </UL>
            <P><B>Pricing:</B> $12/month or $99/year. Free trial for 30 days — no credit card required.</P>
          </Section>

          <Section title="Tips for Getting the Most Out of LandlordLedger" emoji="💡">
            <OL>
              <LI><B>Set up Recurring transactions first.</B> Rent, mortgage, insurance — log these once and your P&L basically runs itself.</LI>
              <LI><B>Use Smart Upload on every receipt.</B> Snap a photo the moment you buy something for a property. It logs the expense AND files the receipt automatically.</LI>
              <LI><B>Check your Dashboard weekly.</B> Five seconds to see how your portfolio is performing.</LI>
              <LI><B>Run your Tax Report in January.</B> Everything is already organized — no scrambling.</LI>
              <LI><B>Use the Document Vault for leases.</B> When a tenant calls about their lease, it's one tap away.</LI>
            </OL>
          </Section>

          <div style={{
            marginTop: 24, paddingTop: 18, borderTop: '1px solid #1e2235',
            color: '#475569', fontSize: 12, fontStyle: 'italic', textAlign: 'center',
          }}>
            LandlordLedger — Built by a landlord, for landlords.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Small presentational helpers ───────────────────────────────────────────
// Kept inline so the manual stays one file. If we ever build a second long-
// form doc (e.g., a Pro onboarding tour), promote these to a shared module.

function Section({ title, emoji, children }) {
  return (
    <section style={{ marginTop: 22 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 700, color: '#3b82f6',
        marginTop: 0, marginBottom: 10,
        paddingBottom: 6, borderBottom: '1px solid #1e2235',
      }}>
        {emoji && <span style={{ marginRight: 8 }}>{emoji}</span>}{title}
      </h2>
      {children}
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={{ marginTop: 14, marginLeft: 0 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#cbd5e1', margin: '12px 0 8px' }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function P({ children }) {
  return <p style={{ margin: '8px 0', fontSize: 14, color: '#cbd5e1', lineHeight: 1.6 }}>{children}</p>;
}

function B({ children }) {
  return <strong style={{ color: '#e2e8f0', fontWeight: 600 }}>{children}</strong>;
}

function UL({ children }) {
  return <ul style={{ margin: '8px 0', paddingLeft: 20, fontSize: 14, color: '#cbd5e1', lineHeight: 1.7 }}>{children}</ul>;
}

function OL({ children }) {
  return <ol style={{ margin: '8px 0', paddingLeft: 22, fontSize: 14, color: '#cbd5e1', lineHeight: 1.7 }}>{children}</ol>;
}

function LI({ children }) {
  return <li style={{ marginBottom: 4 }}>{children}</li>;
}

function Tip({ children }) {
  return (
    <div style={{
      marginTop: 10,
      background: 'rgba(251, 191, 36, 0.08)',
      border: '1px solid rgba(251, 191, 36, 0.3)',
      borderLeft: '3px solid #fbbf24',
      borderRadius: 8,
      padding: '10px 12px',
      color: '#fde68a',
      fontSize: 13,
      lineHeight: 1.55,
    }}>
      <strong style={{ color: '#fbbf24', fontFamily: "'Courier New', monospace", fontSize: 11, letterSpacing: 1, display: 'block', marginBottom: 3 }}>💡 TIP</strong>
      {children}
    </div>
  );
}

function Note({ children }) {
  return (
    <div style={{
      marginTop: 10,
      background: 'rgba(148, 163, 184, 0.06)',
      border: '1px solid #2d3555',
      borderLeft: '3px solid #94a3b8',
      borderRadius: 8,
      padding: '10px 12px',
      color: '#cbd5e1',
      fontSize: 13,
      lineHeight: 1.55,
    }}>
      <strong style={{ color: '#94a3b8', fontFamily: "'Courier New', monospace", fontSize: 11, letterSpacing: 1, display: 'block', marginBottom: 3 }}>📌 NOTE</strong>
      {children}
    </div>
  );
}

function VaultList({ rows }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, margin: '10px 0 12px' }}>
      {rows.map(([folder, desc]) => (
        <div key={folder} style={{
          display: 'flex', gap: 12, alignItems: 'baseline',
          padding: '8px 10px', borderRadius: 8,
          background: '#0f1117', border: '1px solid #1e2235',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', minWidth: 160, flexShrink: 0 }}>
            {folder}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.45 }}>{desc}</div>
        </div>
      ))}
    </div>
  );
}
